"use client";

import { Button, ButtonProps } from "@/components/atoms/Button";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/molecules/Form";
import CalendarIcon from "@/icons/__generated__/Calendar/Calendar20";
import { cn } from "@/lib/utils";
import * as React from "react";
import { useFormContext } from "react-hook-form";
import { DatePicker, DatePickerProps } from "./DatePicker";
import {
  getDisplayedValue,
  hasValue,
  normalizeDateValue,
} from "./date-picker-utils";

const DatePickerFieldTrigger = React.forwardRef<
  HTMLButtonElement,
  ButtonProps & {
    errorMsg?: string;
    readOnly?: boolean;
    value?: Date;
    placeholder?: React.ReactNode;
    dateFormat?: string;
    displayCalendarIcon?: boolean;
  }
>(
  (
    {
      errorMsg,
      fullWidth,
      readOnly,
      disabled,
      value,
      placeholder,
      dateFormat,
      displayCalendarIcon = true,
      ...buttonProps
    },
    ref,
  ) => {
    const dateValue = normalizeDateValue(value);

    return (
      <Button
        variant="outline-1"
        appearance="white"
        prefix={
          displayCalendarIcon ? (
            <CalendarIcon className="tw-text-gray-900" />
          ) : null
        }
        fullWidth={fullWidth}
        disabled={disabled}
        ref={ref as React.RefObject<HTMLButtonElement>}
        {...buttonProps}
        className={cn(
          "tw-justify-start tw-text-left tw-body-2 hover:tw-bg-base-white",
          buttonProps?.className,
          {
            "tw-pointer-events-none": readOnly,
            "tw-text-gray-600": !hasValue(dateValue),
            "tw-border-red-800": !!errorMsg,
          },
        )}
      >
        {getDisplayedValue(dateValue, placeholder, dateFormat)}
      </Button>
    );
  },
);
DatePickerFieldTrigger.displayName = "DatePickerFieldTrigger";

export type DatePickerFieldProps = DatePickerProps & {
  name: string;
  label?: React.ReactNode;
  subcopy?: React.ReactNode;
  placeholder?: React.ReactNode;
  note?: React.ReactNode;
  className?: string;
  disabled?: boolean;
  readOnly?: boolean;
  required?: boolean;
};

export const DatePickerField = ({
  name = "date",
  calendarProps,
  fullWidth,
  label,
  subcopy,
  placeholder,
  note,
  onValueChange,
  className,
  disabled,
  readOnly,
  triggerProps,
  popoverProps,
  testId,
  required,
  dateFormat,
  displayCalendarIcon
}: DatePickerFieldProps) => {
  const { control } = useFormContext();

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => {
        return (
          <FormItem
            disabled={disabled}
            readOnly={readOnly}
            className={cn({ "tw-w-full tw-flex-1": fullWidth }, className)}
          >
            {!!label && (
              <FormLabel subcopy={subcopy} required={required}>
                {label}
              </FormLabel>
            )}
            <DatePicker
              testId={testId}
              dateFormat={dateFormat}
              popoverProps={popoverProps}
              displayCalendarIcon={displayCalendarIcon}
              calendarProps={Object.assign(
                {
                  selected: normalizeDateValue(field.value),
                  onSelect: onValueChange || field.onChange,
                },
                calendarProps || {},
              )}
              trigger={
                <FormControl>
                  <DatePickerFieldTrigger
                    dateFormat={dateFormat}
                    fullWidth={fullWidth}
                    readOnly={readOnly}
                    disabled={disabled}
                    value={field.value}
                    placeholder={placeholder}
                    ref={field.ref}
                    testId={testId ? `${testId}-trigger` : undefined}
                    {...(triggerProps || {})}
                    displayCalendarIcon={displayCalendarIcon}
                  />
                </FormControl>
              }
            />
            <FormMessage>{note}</FormMessage>
          </FormItem>
        );
      }}
    />
  );
};
